import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { Organization } from '@newgenus/common';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { OrgViewToggleComponent, OrgListViewService } from '../../shared/org-view-toggle';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../../../material.module';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent } from '../../shared/org-search-bar/org-search-bar.component';
import {
    buildFeatureCatalog,
    buildPermissionCatalog,
    CatalogRefs,
    FeatureCatalogEntry,
    PermissionCatalogEntry,
} from '../../shared/feature-permission-catalog';
import {
    FeaturePermissionDetailsData,
    OrgFeaturePermissionDetailsDialogComponent,
} from '../org-feature-permission-details-dialog/org-feature-permission-details-dialog';
import { restrictToKeys } from '../../shared/only-keys';

export type FeaturesPermissionsListMode = 'features' | 'permissions';

export interface FeaturesPermissionsListDialogData {
    organization: Organization;
    mode: FeaturesPermissionsListMode;
    /** Only these items (a staff member's dashboard tile, ISS-022); absent = all. */
    onlyKeys?: string[];
}

/** One row of either list, flattened so a single card template serves both modes. */
interface CatalogCard {
    key: string;
    title: string;
    /** Permissions mode: the feature the permission belongs to. */
    featureName?: string;
    description: string;
    /** Features mode: the permissions this feature offers. */
    permissions: { key: string; label: string; description: string }[];
    granted: boolean;
    securityGroups: string[];
    hats: string[];
    staff: string[];
    refs: CatalogRefs;
    searchText: string;
}

/**
 * The Overview's Features and Permissions tiles (ISS-024): a list of the org's features, or of
 * the permissions they offer, each showing which security groups grant it and which hats and
 * staff end up with it. Opened from the org dashboard; read-only — grants are edited on the
 * security group. A card opens its details window, like a hat or security group does.
 */
@Component({
    selector: 'shared-org-features-permissions-list-dialog',
    imports: [MaterialModule, CommonModule, OrgFilterBarComponent, OrgSearchBarComponent, OrgViewToggleComponent],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2" [style.color]="entityColors.getColor(data.mode)">{{ icon }}</mat-icon>
            <span class="dialog-title-text">{{ heading }} ({{ filtered.length }} of {{ all.length }})</span>
            <shared-org-view-toggle></shared-org-view-toggle>
            <button mat-icon-button mat-dialog-close class="close-button" aria-label="Close">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content" [class.ng-list-view]="(listView.mode$ | async) === 'list'">
            <shared-org-search-bar
                [searchLabel]="'Search ' + heading.toLowerCase()"
                [searchPlaceholder]="searchPlaceholder"
                (searchChanged)="onSearchChanged($event)"
            >
            </shared-org-search-bar>

            <shared-org-filter-bar
                [filters]="filterConfig"
                [totalCount]="all.length"
                activeColor="#f57c00"
                activeDarkColor="#ffb74d"
                (filtersChanged)="onFiltersChanged($event)"
            >
            </shared-org-filter-bar>

            <div class="catalog-grid" *ngIf="filtered.length > 0">
                <mat-card
                    *ngFor="let card of filtered; trackBy: trackByKey"
                    class="catalog-card"
                    [attr.data-key]="card.key"
                    (click)="openDetails(card)"
                >
                    <div class="card-identity">
                        <div
                            class="entity-avatar"
                            [ngStyle]="{
                                background:
                                    'linear-gradient(135deg, ' +
                                    entityColors.getColor(data.mode) +
                                    ' 0%, ' +
                                    entityColors.getColor(data.mode) +
                                    'cc 100%)',
                            }"
                        >
                            <mat-icon class="entity-icon">{{ icon }}</mat-icon>
                        </div>
                        <div class="identity-text">
                            <span class="entity-name">{{ card.title }}</span>
                            <span class="entity-subtitle" *ngIf="card.featureName">{{ card.featureName }}</span>
                            <span class="entity-subtitle not-granted" *ngIf="!card.granted">
                                <mat-icon class="subtitle-icon">block</mat-icon>
                                Not given to any security group
                            </span>
                        </div>
                        <button
                            mat-icon-button
                            class="details-button"
                            matTooltip="View details"
                            aria-label="View details"
                            (click)="$event.stopPropagation(); openDetails(card)"
                        >
                            <mat-icon>visibility</mat-icon>
                        </button>
                    </div>

                    <p class="card-description" *ngIf="card.description">{{ card.description }}</p>

                    <div class="badges-row">
                        <span
                            class="badge"
                            *ngIf="data.mode === 'features'"
                            matTooltip="Permissions this feature offers"
                        >
                            <mat-icon class="badge-icon">vpn_key</mat-icon>
                            {{ card.permissions.length }}
                        </span>
                        <span class="badge" matTooltip="Security groups that give it">
                            <mat-icon class="badge-icon">security</mat-icon>
                            {{ card.securityGroups.length }}
                        </span>
                        <span class="badge" matTooltip="Hats that carry it">
                            <mat-icon class="badge-icon">school</mat-icon>
                            {{ card.hats.length }}
                        </span>
                        <span class="badge" matTooltip="Staff who have it">
                            <mat-icon class="badge-icon">person</mat-icon>
                            {{ card.staff.length }}
                        </span>
                    </div>
                </mat-card>
            </div>

            <div *ngIf="filtered.length === 0" class="empty-state">
                <mat-icon class="empty-icon">{{ icon }}</mat-icon>
                <p *ngIf="searchTerm">No {{ heading.toLowerCase() }} found matching "{{ searchTerm }}"</p>
                <p *ngIf="!searchTerm && activeFilters.size">No {{ heading.toLowerCase() }} match the active filters</p>
                <p *ngIf="!searchTerm && !activeFilters.size">No {{ heading.toLowerCase() }} in this organization</p>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
            }
            .dialog-title-text {
                min-width: 0;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
            .close-button {
                margin-left: 4px;
            }
            .dialog-content {
                padding: 16px 24px;
                min-height: 400px;
                max-height: 70vh;
                overflow-y: auto;
            }
            .catalog-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 16px;
                align-items: start;
            }
            .catalog-card {
                cursor: pointer;
                border: 1px solid rgba(0, 0, 0, 0.08);
                display: flex;
                flex-direction: column;
                gap: 8px;
                padding: 12px 16px;
                background-color: #f5f5f5;
                transition:
                    box-shadow 0.2s,
                    border-color 0.2s;
            }
            .catalog-card:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border-color: rgba(0, 0, 0, 0.25);
            }
            .card-identity {
                display: flex;
                align-items: center;
                gap: 12px;
                min-width: 0;
            }
            .entity-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }
            .entity-icon {
                color: white;
                font-size: 22px;
                width: 22px;
                height: 22px;
            }
            .identity-text {
                display: flex;
                flex-direction: column;
                min-width: 0;
                flex: 1;
            }
            .entity-name {
                font-size: 15px;
                font-weight: 600;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            .entity-subtitle {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.55);
                display: flex;
                align-items: center;
                gap: 2px;
            }
            .subtitle-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }
            .details-button {
                flex-shrink: 0;
                color: var(--entity-color-security-groups, #f57c00);
            }
            .card-description {
                margin: 0;
                font-size: 13px;
                color: rgba(0, 0, 0, 0.7);
            }
            .badges-row {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
            }
            .badge {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                padding: 2px 6px;
                border-radius: 12px;
                font-size: 12px;
                background-color: rgba(0, 0, 0, 0.06);
                color: rgba(0, 0, 0, 0.6);
            }
            .badge-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }
            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                color: rgba(0, 0, 0, 0.54);
            }
            .empty-icon {
                font-size: 64px;
                width: 64px;
                height: 64px;
                margin-bottom: 16px;
            }
            .mr-2 {
                margin-right: 8px;
            }

            :host-context(.dark-theme) .catalog-card {
                background-color: #383838;
                border-color: rgba(255, 255, 255, 0.1);
            }
            :host-context(.dark-theme) .entity-subtitle,
            :host-context(.dark-theme) .card-description,
            :host-context(.dark-theme) .empty-state {
                color: rgba(255, 255, 255, 0.6);
            }
            :host-context(.dark-theme) .badge {
                background-color: rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.7);
            }

            /* Phones: full-screen dialog (global .ob-list-dialog); one full-width column. */
            @media (max-width: 767.98px) {
                .dialog-header {
                    padding: 12px 16px;
                    font-size: 18px;
                }
                .dialog-content {
                    padding: 12px 16px;
                    min-height: 0;
                }
                .catalog-grid {
                    grid-template-columns: minmax(0, 1fr);
                    gap: 12px;
                }
                .catalog-grid > * {
                    min-width: 0;
                    max-width: 100%;
                }
            }
        `,
    ],
})
export class OrgFeaturesPermissionsListDialogComponent implements OnInit {
    public entityColors = inject(OrgEntityColorsService);
    public listView = inject(OrgListViewService);
    public all: CatalogCard[] = [];
    public filtered: CatalogCard[] = [];
    public searchTerm = '';
    public activeFilters = new Set<string>();
    public filterConfig: OrgFilterConfig[] = [];
    private dialog = inject(MatDialog);

    constructor(@Inject(MAT_DIALOG_DATA) public data: FeaturesPermissionsListDialogData) {}

    public get heading(): string {
        return this.data.mode === 'features' ? 'Features' : 'Permissions';
    }

    public get icon(): string {
        return this.data.mode === 'features' ? 'extension' : 'vpn_key';
    }

    public get searchPlaceholder(): string {
        return this.data.mode === 'features'
            ? 'Search by feature, permission, security group, hat, staff member...'
            : 'Search by permission, feature, security group, hat, staff member...';
    }

    public ngOnInit(): void {
        const org = this.data?.organization;
        this.all = org
            ? this.data.mode === 'features'
                ? buildFeatureCatalog(org).map(fromFeature)
                : buildPermissionCatalog(org).map(fromPermission)
            : [];
        this.all = restrictToKeys(this.all, this.data?.onlyKeys, (c) => c.key);
        const granted = this.all.filter((c) => c.granted).length;
        this.filterConfig = [
            { key: 'granted', label: 'In use', icon: 'check_circle', count: granted },
            { key: 'notGranted', label: 'Not in use', icon: 'block', count: this.all.length - granted },
        ];
        this.apply();
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.apply();
    }

    public onFiltersChanged(active: Set<string>): void {
        this.activeFilters = new Set(active);
        this.apply();
    }

    public openDetails(card: CatalogCard): void {
        const data: FeaturePermissionDetailsData = {
            organization: this.data.organization,
            mode: this.data.mode,
            item: {
                title: card.title,
                featureName: card.featureName,
                description: card.description,
                permissions: card.permissions,
                granted: card.granted,
                refs: card.refs,
            },
        };
        this.dialog.open(OrgFeaturePermissionDetailsDialogComponent, {
            data,
            width: '85vw',
            height: '85vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            panelClass: 'ob-list-dialog',
        });
    }

    public trackByKey(_: number, card: CatalogCard): string {
        return card.key;
    }

    private apply(): void {
        const term = this.searchTerm.trim().toLowerCase();
        this.filtered = this.all.filter(
            (c) =>
                (!term || c.searchText.includes(term)) &&
                (!this.activeFilters.has('granted') || c.granted) &&
                (!this.activeFilters.has('notGranted') || !c.granted)
        );
    }
}

function searchText(parts: (string | undefined)[]): string {
    return parts.filter(Boolean).join(' ').toLowerCase();
}

function fromFeature(f: FeatureCatalogEntry): CatalogCard {
    return {
        key: f.key,
        title: f.name,
        description: f.description,
        permissions: f.permissions,
        granted: f.securityGroups.length > 0,
        securityGroups: f.securityGroups,
        hats: f.hats,
        staff: f.staff,
        refs: f.refs,
        searchText: searchText([
            f.name,
            f.key,
            f.description,
            ...f.permissions.map((p) => p.label),
            ...f.securityGroups,
            ...f.hats,
            ...f.staff,
        ]),
    };
}

function fromPermission(p: PermissionCatalogEntry): CatalogCard {
    return {
        key: `${p.featureKey}/${p.key}`,
        title: p.label,
        featureName: p.featureName,
        description: p.description,
        permissions: [],
        granted: p.granted,
        securityGroups: p.securityGroups,
        hats: p.hats,
        staff: p.staff,
        refs: p.refs,
        searchText: searchText([
            p.label,
            p.key,
            p.featureName,
            p.description,
            ...p.securityGroups,
            ...p.hats,
            ...p.staff,
        ]),
    };
}
